import { chronosphereLogsClient, useDeepMemo, useFeatureFlags } from '@chronosphereio/core';
import { useMemoQueriesResults } from '@chronosphereio/shared';
import { QueryKey, useQueries } from '@tanstack/react-query';
import { produce } from 'immer';
import { useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  DATA_QUERY_KEY,
  dataResponseSchema,
  ErrorState,
  FormulaDefinition,
  Notice,
  QueryDefinition,
  SupportedDataType,
} from '@/core';
import { useSuggestedStepMs } from '@/plugin-system/runtime/time/time';
import { calculateStatsAndCommonLabels } from '../../utils/stats-common-labels';
import { useDataSnapshotContext } from '../DataSnapshotProvider/DataSnapshotContext';
import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  PaginationConfig,
  UseDataQueryOptions,
  useHandleQueryModeChanges,
  useRunDataQueries,
} from '../data-querying';
import { QueryTruncationProvider, useQueryTruncation } from '../QueryTruncationProvider';
import { DataQueriesContext } from './DataQueriesContext';
import { appendFormulaResults, useFormulaParser } from './formulas';
import {
  DataQueriesProviderProps,
  DataQueryConfig,
  DataQueryFunction,
  Pagination,
  QueryData,
  transformQueryResults,
  UseDataQueryResult,
} from './model';
import { SnapshotDataQueryProvider } from './SnapshotDataQueryProvider';

const EMPTY_FORMULAS: FormulaDefinition[] = [];

export interface DataQueriesState {
  formulas: FormulaDefinition[];
  dataQueryResults: UseDataQueryResult<SupportedDataType | SupportedDataType[]>;
}

/*
 * Hook to fetch data from the nearest DataQueriesProvider available in the component tree
 * @param queryFormat Specify the query format you want to retrieve from the DataQueriesProvider. Currently we only support TimeSeriesQuery, but we have plans to support other formats in the future.
 * @returns Array of the results of data queries. Each element in the array represents the result of a specific data query.
 */
interface UseDataQueryResultArgs<T extends SupportedDataType | SupportedDataType[]> {
  dataTypeFilter?: T;
  /**
   * Include relevant statistics in the result.
   * Can be used to apply optimizations based on total series or datapoints.
   * Supported for TimeSeries, SingleValue, SingleValueTimeSeries data types.
   * Other data types are silently ignored (contribute 0 to stats calculations).
   */
  includeStats?: boolean;
  /**
   * Include common labels (labels shared across all series/values) in the result.
   * Can be used to emphasize unique labels (ex: declutter series names in the tooltip).
   * Supported for TimeSeries, SingleValue, and SingleValueTimeSeries data types.
   * Other data types are silently ignored (do not contribute to common label detection).
   */
  includeCommonLabels?: boolean;
}

interface QueryResultsCache {
  queryResults?: QueryData[];
  pagination?: Pagination;
  errors: unknown[];
}

export function useProcessDataQueryResultsInternal(ctx: DataQueriesState) {
  const { dataQueryResults, formulas } = ctx;

  const { enableVizFrameworkFormulas: enableFormulas, enableDataQueriesLoadingCacheCheck } = useFeatureFlags();

  // Cache the query results when the query is not fetching
  const queryResultsCache = useRef<QueryResultsCache | undefined>();

  const { data: parseFormula, isError: isParseFormulaLoadingError } = useFormulaParser({
    enabled: formulas.length > 0,
  });
  if (isParseFormulaLoadingError && formulas.length > 0) {
    // if someone is using formulas and for whatever reason the chunk doesn't load,
    // it's a fatal error.
    throw new Error('Failed to load formulas plugin');
  }

  // Create a new context object with the filtered results and errors
  const resultCtx = useMemo(() => {
    // Keep track of whether any query reports initial loading state prior to cache adjustment.
    let isAnyInitialLoading = false;

    // Filter the query results based on the specified query format
    const queryResultWithLoadingState = dataQueryResults.queryResults.map((queryResult) => {
      // Track raw loading states before modification (for cache decision).
      if (queryResult.isLoading) {
        // eslint-disable-next-line react-hooks/immutability
        isAnyInitialLoading = true;
      }
      return {
        ...queryResult,
        isLoading: queryResult.isLoading && !queryResultsCache.current,
      };
    });

    const isFetching = queryResultWithLoadingState.some((result) => result.isFetching);
    const isFetchingMore = queryResultWithLoadingState.some((result) => result.isFetchingMore);

    // We are only loading if the query is fetching and we don't have any cached results
    const isLoading = queryResultWithLoadingState.some((result) => result.isLoading) && !queryResultsCache.current;

    const isSuspended = dataQueryResults.queryMode === 'suspended';
    const isUnmounted = dataQueryResults.queryMode === 'unmounted';
    const hasDataOrError = queryResultWithLoadingState.some((result) => !!(result.data || result.error));

    // Only update the query results in the following cases:
    // - Queries are done fetching/loading, the queries are not suspended, and there is data or an error, so it's worth
    //   updating with the new data/error.
    // - Queries are done fetching/loading and the queries are unmounted. There won't be a data/error in this case, and
    //   we want to update the cache to purge the unmounted results.
    if (!isFetching && !isLoading && !isSuspended && (hasDataOrError || isUnmounted)) {
      queryResultsCache.current = {
        queryResults: queryResultWithLoadingState,
        pagination: dataQueryResults.pagination,
        errors: dataQueryResults.errors,
      };
    }

    // Use cached results while fetching, suspended, or while queries are in their initial loading state and we have a cache.
    // This catches cases such as variable dependency changes where queries are disabled/pending and
    // would otherwise briefly show empty results (flash 'No data.' as the loading state).
    const isAnyInitialLoadingAndCacheExists =
      enableDataQueriesLoadingCacheCheck && isAnyInitialLoading && !!queryResultsCache.current;
    const shouldUseCacheIfExists = isFetching || isSuspended || isAnyInitialLoadingAndCacheExists;

    // Return the cached query results if the query is fetching and we have existing results
    const queryResultsPostCaching =
      shouldUseCacheIfExists && queryResultsCache.current?.queryResults
        ? queryResultsCache.current.queryResults
        : queryResultWithLoadingState;

    const queryResultsWithFormulaResults = enableFormulas
      ? appendFormulaResults({
          result: queryResultsPostCaching,
          formulas: formulas,
          parseFormula,
          isLoading,
          isFetching,
          isFetchingMore,
        })
      : queryResultsPostCaching;

    const queryResults = queryResultsWithFormulaResults.filter((queryResult) => {
      // filter out disabled queries post formula processing
      if (enableFormulas && formulas.length > 0) {
        return !queryResult.definition?.spec.disabled;
      }
      return true;
    });

    const pagination =
      shouldUseCacheIfExists && queryResultsCache.current?.pagination
        ? queryResultsCache.current.pagination
        : dataQueryResults.pagination;
    const errors =
      shouldUseCacheIfExists && queryResultsCache.current?.errors
        ? queryResultsCache.current?.errors
        : dataQueryResults.errors;

    return {
      queryKey: dataQueryResults.queryKey,
      queryResults: queryResults,
      isFetching,
      isFetchingMore,
      isLoading,
      refetchAll: dataQueryResults.refetchAll,
      errors,
      pagination,
      queryMode: dataQueryResults.queryMode,
      id: dataQueryResults.id,
      cancelInProgressDataQueries: dataQueryResults.cancelInProgressDataQueries,
    };
  }, [
    dataQueryResults.queryResults,
    dataQueryResults.queryMode,
    dataQueryResults.pagination,
    dataQueryResults.errors,
    dataQueryResults.queryKey,
    dataQueryResults.refetchAll,
    dataQueryResults.id,
    dataQueryResults.cancelInProgressDataQueries,
    enableDataQueriesLoadingCacheCheck,
    enableFormulas,
    formulas,
    parseFormula,
  ]);

  return resultCtx;
}

export function useDataQueryResult<T extends SupportedDataType | SupportedDataType[]>(
  args?: UseDataQueryResultArgs<T>
): UseDataQueryResult<T> {
  const ctx = useDataQueriesContext();
  const filteredCtx = useMemo(
    () =>
      produce(ctx, (draft) => {
        draft.queryResults = draft.queryResults.filter((queryResult) => {
          // return result if query is loading for the first time or dataTypeFilter is undefined
          if ((queryResult.isLoading && queryResult.data === undefined) || !args?.dataTypeFilter) {
            return true;
          }

          if (Array.isArray(args?.dataTypeFilter) && queryResult.type !== undefined) {
            return args?.dataTypeFilter.includes(queryResult.type);
          }

          return args?.dataTypeFilter === queryResult?.type;
        });
      }) as UseDataQueryResult<T>,

    // We only want to update the filtered context when the query results or the dataTypeFilter changes
    [ctx, args?.dataTypeFilter]
  );

  const { stats, commonLabels } = useMemo(
    () =>
      calculateStatsAndCommonLabels({
        queryResults: filteredCtx.queryResults,
        shouldCalcStats: args?.includeStats ?? false,
        shouldCalcCommonLabels: args?.includeCommonLabels ?? false,
      }),
    [args?.includeStats, args?.includeCommonLabels, filteredCtx.queryResults]
  );

  const result = { ...filteredCtx };
  if (stats) result.stats = stats;
  if (commonLabels) result.commonLabels = commonLabels;
  return result;
}

/**
 * If you need to query data from a datasource, you can use the useDataQueries hook.
 * It fetches the data from the nearest DataQueriesProvider available in the component tree, utilizing the time range set in the TimeRangeProvider.
 * @param queries Array of queries (either query definition or custom query function)
 * @param options An optional object that can be used to configure options in query plugin.
 * @param children Optional React elements or components to be rendered within the provider
 * @param queryOptions An optional object that can be used to configure options in useQuery.
 */
export function DataQueriesProvider(props: DataQueriesProviderProps) {
  const { children, options, ...rest } = props;
  const snapshotContext = useDataSnapshotContext();
  const snapshotId = options?.snapshotId;

  // Determine if we should use snapshot provider
  const shouldUseSnapshot = snapshotId && snapshotContext;

  return (
    <QueryTruncationProvider>
      {shouldUseSnapshot ? (
        <SnapshotDataQueryProvider snapshotId={snapshotId} id={rest.id}>
          {children}
        </SnapshotDataQueryProvider>
      ) : (
        <DataQueryProviderCore {...rest} options={options}>
          {children}
        </DataQueryProviderCore>
      )}
    </QueryTruncationProvider>
  );
}

type UseDataQueriesArgs = Omit<DataQueriesProviderProps, 'children'>;

type UseDataQueriesIdResponse = {
  id: string;
  /**
   * Base of the query key used for all associated data queries. Helpful for doing things like cancellation.
   */
  baseQueryKey: QueryKey;
};

const useDataQueriesId = ({ id: initId }: Pick<UseDataQueriesArgs, 'id'>): UseDataQueriesIdResponse => {
  // Ensure all data query providers have an id, so we can easily cancel/reset/etc. all queries without having to keep
  // track of the full list of query keys. Uses the passed in id if one is provided. Otherwise, falls back to a
  // react-provided unique id.
  const fallbackId = useId();
  const id = initId ?? fallbackId;

  return useMemo(() => {
    return {
      id,
      baseQueryKey: [DATA_QUERY_KEY, id],
    };
  }, [id]);
};

/*
  This hook is intended to be used to debug that schema validation is working as we expect. 

  This should never be enabled for production as it could have some performance impact
*/
const useValidateResultsAgainstSchema = (
  results: ReturnType<typeof useRunDataQueries>,
  queryDefinitions: QueryDefinition[]
) => {
  const hasChecked = useRef(false);
  const { enableDataQueryResponseSchemaValidation } = useFeatureFlags();
  useEffect(() => {
    // If the feature flag is enabled and all the queries are done loading, try and validate the response against the schema
    if (
      enableDataQueryResponseSchemaValidation &&
      !hasChecked.current &&
      results.every((r) => r.isLoading === false && r.isFetching === false && !r.data?.internalMetadata?.isFetchingMore)
    ) {
      hasChecked.current = true;
      results.forEach((result, index) => {
        const queryDefinition = queryDefinitions[index];
        const schemaResult = dataResponseSchema.safeParse(result.data);
        if (result.data !== undefined && schemaResult.error !== undefined) {
          chronosphereLogsClient.track('data_query_response_schema_mismatch', {
            error: schemaResult.error.message,
            queryDefinition,
          });
        } else if (schemaResult.data !== undefined) {
          // Serialize it. Unserialize it.  Confirm it still passes parse
          const serializedData = JSON.stringify(result.data);
          const unserializedData = JSON.parse(serializedData);

          const unserializedResult = dataResponseSchema.safeParse(unserializedData);

          if (unserializedResult.error !== undefined) {
            chronosphereLogsClient.track('data_query_response_schema_mismatch_after_serialization', {
              error: unserializedResult.error.message,
              queryDefinition,
            });
          }
        }
      });
    }
  }, [results, enableDataQueryResponseSchemaValidation, queryDefinitions]);
};

/**
 * A hook alternative to DataQueriesProvider that can be used to directly fetch DataQuery results.
 */
export function useDataQueries(args: UseDataQueriesArgs) {
  const {
    queries,
    options: propOptions,
    queryOptions,
    formulas,
    id: initId,
    __experimentalTransformQueryResults,
  } = args;

  const { id, baseQueryKey } = useDataQueriesId({ id: initId });

  // If there is no step interval provided, we use the default step interval for the current time range.
  const defaultStepIntervalMs = useSuggestedStepMs({ resolution: propOptions?.resolution });

  // We use deep memo to avoid re-rendering when the queries are the same when provided directly
  // in the props. This prevents the need for the consumer to useMemo the queries props themselves.
  // Given the objects are relatively small, this should have minimal performance impact.
  // Context: https://chronosphereio.slack.com/archives/C059R42SYLB/p1731702023489779
  const { queryDefinitions, queryFunctions } = useDeepMemo(() => {
    const queryFunctions: DataQueryFunction[] = [];
    const queryPluginDefinitions: DataQueryConfig[] = [];
    for (let i = 0; i < queries.length; i++) {
      const query = queries[i];
      if (query === undefined) {
        throw new Error('Query is undefined');
      }
      if ((query as DataQueryFunction).queryFn) {
        queryFunctions.push(query as DataQueryFunction);
      } else {
        queryPluginDefinitions.push(query as DataQueryConfig);
      }
    }

    // Convert plugin spec to the full query definition
    const queryDefinitions: QueryDefinition[] = queryPluginDefinitions?.map((query) => {
      return {
        kind: 'DataQuery',
        spec: {
          plugin: query.definition,
          alias: query.alias,
          disabled: query.disabled,
          id: query.id,
        },
      };
    });

    return {
      queryDefinitions,
      queryFunctions,
    };
  }, [queries]);

  const [pagination, setPagination] = useState<PaginationConfig>({
    page: DEFAULT_PAGE_NUMBER,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Controls backend query limits which can be disabled from notice action button.
  const queryTruncation = useQueryTruncation();
  // Allows consumers to use a different query limit by setting it in options.
  // For example, query truncation works differently in monitor page
  const queryLimits = propOptions?.queryLimits ?? queryTruncation?.queryLimits;

  const options: UseDataQueryOptions = useMemo(() => {
    return {
      suggestedStepMs: defaultStepIntervalMs,
      ...propOptions,
      pagination,
      queryLimits,
    };
  }, [defaultStepIntervalMs, propOptions, pagination, queryLimits]);

  const results = useRunDataQueries(queryDefinitions, formulas, options, queryOptions, id);

  useValidateResultsAgainstSchema(results, queryDefinitions);

  const { queryMode, cancelInProgressDataQueries } = useHandleQueryModeChanges({
    baseQueryKey,
    queryMode: propOptions?.queryMode,
  });

  // A key to identify when the query results have been fetched. We can use the
  // dataUpdatedAt timestamp from the query results to identify when the data has changed.
  const queryKey = results.map((result) => result.dataUpdatedAt).join(',');
  const queryFnResults = useMemoQueriesResults(useQueries({ queries: queryFunctions }));
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const ctx = useMemo(() => {
    const mergedQueryResults = transformQueryResults(results, queryDefinitions).concat(
      transformQueryResults(queryFnResults)
    );
    const refetchAll = () => {
      mergedQueryResults.forEach((result) => result.refetch && result.refetch());
    };

    let paginated = false;
    let totalRowCount: number | undefined = 0;
    let hasNextPage;
    mergedQueryResults.map((result) => {
      // If pagination is defined, it indicates that there is only one query result.
      // We disable pagination when there are multiple queries to prevent weird edge cases.
      if (result.data?.metadata?.pagination) {
        paginated = true;
        if (result.data.metadata?.pagination.totalRowCount) {
          totalRowCount = result.data.metadata?.pagination.totalRowCount;
        } else {
          totalRowCount = undefined;
          hasNextPage = !!result.data?.metadata?.pagination?.cursor;
        }
      }
    });

    const errors = mergedQueryResults.map((result) => result.error).filter((error) => error);
    return {
      formulas: formulas ?? EMPTY_FORMULAS,
      dataQueryResults: {
        queryKey,
        queryResults: __experimentalTransformQueryResults
          ? __experimentalTransformQueryResults(mergedQueryResults)
          : mergedQueryResults,
        isFetching: mergedQueryResults.some((result) => result.isFetching),
        isFetchingMore: mergedQueryResults.some((result) => result.isFetchingMore),
        isLoading: mergedQueryResults.some((result) => result.isLoading),
        refetchAll,
        errors,
        pagination: paginated
          ? {
              ...pagination,
              hasNextPage,
              totalRowCount,
              setPagination,
            }
          : undefined,
        queryLimits,
        queryMode,
        id,
        cancelInProgressDataQueries,
      },
    };
  }, [
    results,
    queryDefinitions,
    queryFnResults,
    formulas,
    queryKey,
    __experimentalTransformQueryResults,
    pagination,
    queryLimits,
    queryMode,
    id,
    cancelInProgressDataQueries,
  ]);

  const processedCtx = useProcessDataQueryResultsInternal(ctx);
  return processedCtx;
}

function DataQueryProviderCore({ children, ...args }: UseDataQueriesArgs & { children?: React.ReactNode }) {
  const value = useDataQueries(args);
  return <DataQueriesContext.Provider value={value}>{children}</DataQueriesContext.Provider>;
}

function useDataQueriesContext(): UseDataQueryResult {
  const ctx = useContext(DataQueriesContext);
  if (ctx === undefined) {
    throw new Error('useDataQueriesContext must be used within a DataQueriesProvider');
  }
  return ctx;
}

export function useHasDataQueriesContext(): boolean {
  const ctx = useContext(DataQueriesContext);
  return ctx !== undefined;
}

export type UseDataQueryNoticesResult = Pick<
  UseDataQueryResult,
  'errors' | 'isFetching' | 'isFetchingMore' | 'isLoading'
> & {
  notices: Notice[];
  /**
   * Indicates the error state of the data query results.
   */
  errorState: ErrorState;
};

/**
 * Hook to fetch notices and errors from data query results.
 */
export function useDataQueryNotices(): UseDataQueryNoticesResult {
  // Processing the data queries in a separate hook avoids potential issues with the
  // sometimes very large queryResults accidentally being kept around too long and
  // causing memory issues because of closures in the react component.
  const { isFetching, isFetchingMore, isLoading, errors, queryResults } = useDataQueryResult();

  const notices = useMemo(
    () => queryResults.map((result) => result.data?.metadata?.notices ?? []).flat(),
    [queryResults]
  );

  const errorState = useMemo(() => {
    if (!errors.length) {
      // There are no errors, so nothing is broken.
      return 'none';
    }

    if (isLoading) {
      // Cannot be sure things are fully broken until all queries complete.
      // We use `isLoading` (the first fetch) instead of `isFetching` (run on every fetch) to avoid
      // flashes of state when refreshing data.
      return 'partial';
    }

    // If there is some data that can be rendered, we should try to show it.
    const hasData = queryResults.some((queryResult) => {
      return queryResult.data;
    });
    if (hasData) {
      return 'partial';
    }

    // There is no data and there are errors. The panel is broken and should show full errors.
    return 'critical';
  }, [errors.length, isLoading, queryResults]);

  return {
    notices,
    errors,
    errorState,
    isFetching,
    isFetchingMore,
    isLoading,
  };
}
